/**
 * Tracks in-flight app HTTP calls for a global branded loader.
 * Excludes the auth refresh-token endpoint (see {@link shouldAttachGlobalApiLoader}).
 */

let depth = 0;
/** @type {Set<() => void>} */
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

/** @returns {boolean} */
export function isGlobalApiLoading() {
  return depth > 0;
}

/**
 * @param {(active: boolean) => void} cb — called with true when any tracked request is in flight
 * @returns {() => void} unsubscribe
 */
export function subscribeGlobalApiLoading(cb) {
  const wrap = () => cb(depth > 0);
  listeners.add(wrap);
  wrap();
  return () => listeners.delete(wrap);
}

export function globalApiLoadingBegin() {
  depth += 1;
  emit();
}

export function globalApiLoadingEnd() {
  depth = Math.max(0, depth - 1);
  emit();
}

/**
 * Attach global loader for this URL unless skipped or it is the token refresh API.
 * @param {string} url — absolute or relative URL string
 * @param {{ skipGlobalLoader?: boolean }} [options]
 */
export function shouldAttachGlobalApiLoader(url, options = {}) {
  if (options.skipGlobalLoader) return false;
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  if (lower.includes('refreshtoken')) return false;
  return true;
}
